import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Share2, Copy, Check, X, Facebook, Twitter, Linkedin,
  MessageCircle, Mail, Send, Music2, Instagram, Link as LinkIcon,
} from "lucide-react";

/**
 * ShareMenu — a reusable share widget with dropdown of social channels.
 *
 * Props:
 *   url       — required, the URL to share
 *   title     — optional page/content title (used in the shared caption)
 *   text      — optional descriptive text prepended to the URL when sharing
 *   variant   — "button" (default, opens dropdown) | "inline" (row of icons)
 *   className — override styling on the trigger button
 *
 * Note on Instagram & TikTok: both platforms *don't* support arbitrary
 * URL sharing from the web. We copy the link + description to the clipboard
 * and open the app/site so the user can paste it into their post/story.
 */
export default function ShareMenu({ url, title = "", text = "", variant = "button", className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = text || title || "Check this out on ADN Local FanFest";
  const combined = `${shareText} ${shareUrl}`.trim();

  const enc = encodeURIComponent;
  const targets = [
    { key: "facebook",  label: "Facebook",  icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(shareText)}`,
      brand: "bg-[#1877F2]" },
    { key: "x",         label: "X (Twitter)", icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareText)}`,
      brand: "bg-black" },
    { key: "linkedin",  label: "LinkedIn",  icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
      brand: "bg-[#0A66C2]" },
    { key: "whatsapp",  label: "WhatsApp",  icon: MessageCircle,
      href: `https://wa.me/?text=${enc(combined)}`,
      brand: "bg-[#25D366]" },
    { key: "telegram",  label: "Telegram",  icon: Send,
      href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`,
      brand: "bg-[#229ED9]" },
    { key: "sms",       label: "SMS",       icon: MessageCircle,
      href: `sms:?&body=${enc(combined)}`,
      brand: "bg-[#34C759]" },
    { key: "email",     label: "Email",     icon: Mail,
      href: `mailto:?subject=${enc(title || "ADN Local FanFest")}&body=${enc(combined)}`,
      brand: "bg-[#EA4335]" },
  ];

  // Instagram + TikTok can't accept URL params — copy + open the app
  const copyThenOpen = (openUrl, label) => async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(combined);
      toast.success(`Link copied — paste into your ${label} post`);
    } catch { /* ignore */ }
    window.open(openUrl, "_blank", "noopener,noreferrer");
    setOpen(false);
  };
  const copyOnly = ["instagram", "tiktok"];
  targets.push(
    { key: "instagram", label: "Instagram", icon: Instagram,
      onClick: copyThenOpen("https://www.instagram.com/", "Instagram"),
      brand: "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600" },
    { key: "tiktok",    label: "TikTok",    icon: Music2,
      onClick: copyThenOpen("https://www.tiktok.com/upload", "TikTok"),
      brand: "bg-black" },
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
        setOpen(false);
        return true;
      } catch { /* user cancelled */ }
    }
    return false;
  };

  const onTriggerClick = async () => {
    // On mobile, prefer native share sheet; if unavailable/declined, open the menu
    if (typeof navigator !== "undefined" && navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      const ok = await nativeShare();
      if (ok) return;
    }
    setOpen(o => !o);
  };

  if (variant === "inline") {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`} data-testid="share-inline">
        {targets.map(t => <Btn key={t.key} t={t} copyOnly={copyOnly}/>)}
        <button type="button" onClick={copy} data-testid="share-copy-link-btn"
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
          title="Copy link">
          {copied ? <Check size={16} className="text-primary"/> : <LinkIcon size={16}/>}
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={onTriggerClick} data-testid="share-menu-trigger"
        className={className || "inline-flex items-center gap-2 px-6 py-4 border border-white/25 rounded-sm hover:border-primary min-h-[56px]"}>
        <Share2 size={16}/> Share
      </button>
      {open && (
        <div className="absolute right-0 mt-2 z-50 w-72 bg-[#0d1428] border border-adn-border rounded-lg shadow-xl p-3"
          data-testid="share-menu-panel">
          <div className="flex items-center justify-between px-1 pb-2 mb-2 border-b border-white/10">
            <div className="text-xs uppercase tracking-widest text-white/60">Share via</div>
            <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white" aria-label="Close"><X size={14}/></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {targets.map(t => <Btn key={t.key} t={t} copyOnly={copyOnly} labelBelow/>)}
          </div>
          <button type="button" onClick={copy} data-testid="share-copy-link-btn"
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 rounded text-sm">
            {copied ? <Check size={14} className="text-primary"/> : <Copy size={14}/>}
            <span className="truncate flex-1 text-left">{copied ? "Link copied!" : "Copy link"}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Btn({ t, copyOnly, labelBelow }) {
  const isCopyOnly = copyOnly.includes(t.key);
  const commonCls = `w-12 h-12 rounded-full ${t.brand} flex items-center justify-center text-white hover:brightness-110 transition`;
  const content = <t.icon size={18}/>;
  return (
    <div className={labelBelow ? "flex flex-col items-center gap-1" : ""}>
      {isCopyOnly ? (
        <button type="button" onClick={t.onClick} className={commonCls}
          data-testid={`share-${t.key}`} title={t.label}>{content}</button>
      ) : (
        <a href={t.href} target="_blank" rel="noopener noreferrer" className={commonCls}
          data-testid={`share-${t.key}`} title={t.label}>{content}</a>
      )}
      {labelBelow && <div className="text-[10px] text-white/60">{t.label}</div>}
    </div>
  );
}
